import { useMemo } from 'react';

import { FONT_BASE, SUFFIX_RATIO } from '../constants.js';
import { measureFontBaseline } from '../utils/measure-font-baseline.js';

const SPACING_RATIO_THRESHOLD = 1.5; // Ratio threshold when horizontal space is significantly larger than vertical
const MAX_HORIZONTAL_WEIGHT = 0.8; // Maximum weight to apply to horizontal dimension
const BASE_HORIZONTAL_WEIGHT = 0.5; // Base weight for horizontal dimension
const WEIGHT_ADJUSTMENT_FACTOR = 0.1; // How quickly weight increases with spacing ratio
const VERTICAL_CONSTRAINT_FACTOR = 1.1; // Maximum allowed increase over vertical constraint

/**
 * Calculates the value font size for the given string inputs to fit into a container
 *
 * We can assume that the space needed scales linearly with the font size
 * therefore we can measure a text with font size 10 and interpolate
 * the font size with a given width and height
 * the smaller fontsize for a given width and a given height is the perfect fit
 *
 * @param container - container element for the text
 * @param text - string to measure the font size
 * @param element - element containing text font style
 * @param suffix - string to measure the suffix font size
 * @param suffixElement - element containing suffix font style
 *
 * @returns perfect font size
 */
export function useFontSizeForContainer({
  container,
  text,
  element,
  suffix = '',
  suffixElement,
}: {
  container: { width: number; height: number };
  text: string;
  element?: HTMLElement | null;
  suffix?: string;
  suffixElement?: HTMLElement | null;
}) {
  const style = element
    ? getComputedStyle(element)
    : ({} as CSSStyleDeclaration);

  const valueBaseline = useMemo(
    () =>
      measureFontBaseline({
        text: text,
        fontStyle: style.fontStyle,
        fontWeight: style.fontWeight,
        fontFamily: style.fontFamily,
      }),
    [text, style.fontStyle, style.fontWeight, style.fontFamily],
  );

  const suffixStyle = suffixElement
    ? getComputedStyle(suffixElement)
    : ({} as CSSStyleDeclaration);

  const suffixBaseline = useMemo(
    () =>
      measureFontBaseline({
        text: suffix,
        fontStyle: suffixStyle.fontStyle,
        fontWeight: suffixStyle.fontWeight,
        fontFamily: suffixStyle.fontFamily,
      }),
    [
      suffix,
      suffixStyle.fontStyle,
      suffixStyle.fontWeight,
      suffixStyle.fontFamily,
    ],
  );

  /**
   * Calculates a weighted font size that allows text to use more available space
   * when there's an excess of horizontal space compared to vertical space.
   */
  function calculateWeightedFontSize(
    fontSizeFitHorizontal: number,
    fontSizeFitVertical: number,
    spacingRatio: number,
  ): number {
    // Weight increases as the spacingRatio increases (more horizontal space)
    const horizontalWeight = Math.min(
      MAX_HORIZONTAL_WEIGHT,
      BASE_HORIZONTAL_WEIGHT +
        (spacingRatio - SPACING_RATIO_THRESHOLD) * WEIGHT_ADJUSTMENT_FACTOR,
    );
    const verticalWeight = 1 - horizontalWeight;

    // Calculate a weighted average that favors the horizontal dimension
    // when there's excess horizontal space
    const weightedAverage =
      fontSizeFitHorizontal * horizontalWeight +
      fontSizeFitVertical * verticalWeight;

    // Ensure we don't exceed vertical constraint by too much
    return Math.min(
      weightedAverage,
      fontSizeFitVertical * VERTICAL_CONSTRAINT_FACTOR,
    );
  }

  function calculateStandardFontSize(
    fontSizeFitHorizontal: number,
    fontSizeFitVertical: number,
  ): number {
    return Math.min(fontSizeFitHorizontal, fontSizeFitVertical);
  }

  if (valueBaseline) {
    const suffixWidth = suffixBaseline
      ? (suffixBaseline.width / FONT_BASE) * SUFFIX_RATIO
      : 0;

    const fontSizeFitHorizontal =
      container.width / (valueBaseline.width / FONT_BASE + suffixWidth);
    const fontSizeFitVertical =
      container.height / (valueBaseline.height / FONT_BASE);

    const spacingRatio = fontSizeFitHorizontal / fontSizeFitVertical;

    let preferredFontSize;

    // If there's significantly more horizontal space available than needed
    // compared to vertical space, we should use a more balanced approach
    if (spacingRatio > SPACING_RATIO_THRESHOLD) {
      preferredFontSize = calculateWeightedFontSize(
        fontSizeFitHorizontal,
        fontSizeFitVertical,
        spacingRatio,
      );
    } else {
      preferredFontSize = calculateStandardFontSize(
        fontSizeFitHorizontal,
        fontSizeFitVertical,
      );
    }

    return isFinite(preferredFontSize) ? preferredFontSize : 0;
  }

  return 0;
}
